import { FlightSearchByReservationsPayload, FlightTicketPriceResult } from '@/flight-grid/flight-grid.model';

export const TICKET_PRICE_SEARCH_CACHE_KEY = 'ticketPriceSearchCache';

export interface TicketPriceSearchCacheEntry {
	payload: FlightSearchByReservationsPayload;
	searchId?: number;
	results?: FlightTicketPriceResult[];
}

/**
 * Returns all ticket price search cache entries stored in sessionStorage.
 *
 * @returns Cached ticket price search entries or an empty array when the cache is missing or invalid.
 */
export function getTicketPriceSearchCache(): TicketPriceSearchCacheEntry[] {
	try {
		const raw = sessionStorage.getItem(TICKET_PRICE_SEARCH_CACHE_KEY);
		if (!raw) {
			return [];
		}

		const parsed: unknown = JSON.parse(raw);
		if (!Array.isArray(parsed)) {
			return [];
		}

		return parsed as TicketPriceSearchCacheEntry[];
	} catch {
		return [];
	}
}

/**
 * Finds a cached ticket price search matching the given reservation IDs.
 *
 * @param reservationIds - Reservation IDs sent in {@link FlightSearchByReservationsPayload.reservationIds}.
 * @returns Matching cache entry or `null` when none exists.
 */
export function findTicketPriceSearchCacheEntry(reservationIds: number[]): TicketPriceSearchCacheEntry | null {
	return (
		getTicketPriceSearchCache().find((entry) =>
			areSameReservationIds(entry.payload?.reservationIds, reservationIds),
		) ?? null
	);
}

/**
 * Saves a search ID for the given reservation IDs and removes previously cached results for that payload.
 *
 * @param reservationIds - Reservation IDs sent in {@link FlightSearchByReservationsPayload.reservationIds}.
 * @param searchId - Identifier of the started ticket price search.
 */
export function saveTicketPriceSearchId(reservationIds: number[], searchId: number): void {
	upsertTicketPriceSearchCacheEntry({
		payload: { reservationIds: [...reservationIds] },
		searchId,
	});
}

/**
 * Saves ticket price results for the given reservation IDs and removes the related search ID.
 *
 * @param reservationIds - Reservation IDs sent in {@link FlightSearchByReservationsPayload.reservationIds}.
 * @param results - Ticket price results to persist for later dialog openings.
 */
export function saveTicketPriceSearchResults(reservationIds: number[], results: FlightTicketPriceResult[]): void {
	upsertTicketPriceSearchCacheEntry({
		payload: { reservationIds: [...reservationIds] },
		results,
	});
}

/**
 * Removes a cached ticket price search matching the given reservation IDs.
 *
 * @param reservationIds - Reservation IDs sent in {@link FlightSearchByReservationsPayload.reservationIds}.
 */
export function removeTicketPriceSearchCacheEntry(reservationIds: number[]): void {
	const cache = getTicketPriceSearchCache().filter(
		(entry) => !areSameReservationIds(entry.payload?.reservationIds, reservationIds),
	);
	sessionStorage.setItem(TICKET_PRICE_SEARCH_CACHE_KEY, JSON.stringify(cache));
}

function upsertTicketPriceSearchCacheEntry(nextEntry: TicketPriceSearchCacheEntry): void {
	const cache = getTicketPriceSearchCache();
	const index = cache.findIndex((entry) =>
		areSameReservationIds(entry.payload?.reservationIds, nextEntry.payload.reservationIds),
	);

	if (index >= 0) {
		cache[index] = nextEntry;
	} else {
		cache.push(nextEntry);
	}

	sessionStorage.setItem(TICKET_PRICE_SEARCH_CACHE_KEY, JSON.stringify(cache));
}

function areSameReservationIds(left: number[] | undefined, right: number[]): boolean {
	if (!left?.length || left.length !== right.length) {
		return false;
	}

	return getReservationIdsKey(left) === getReservationIdsKey(right);
}

function getReservationIdsKey(reservationIds: number[]): string {
	return [...reservationIds].sort((left, right) => left - right).join(',');
}
